import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, filter, first, switchMap } from 'rxjs/operators';

import { transformNurseryDataLatLong } from '../helpers/dataTransform.helper';
import { Nursery } from '../models/Nursery';
import { Location } from '../models/Location';
import { LocationService } from './location.service';
import { StatesService } from './states.service';
import { Product } from 'app/models/Product';

const urlEncodedHeaders = new HttpHeaders({
  'Content-Type': 'application/x-www-form-urlencoded'
});

function toFormData(obj: any): FormData {
  const form = new FormData();
  for (let key in obj) {
    form.append(key, obj[key]);
  }
  return form;
}

@Injectable()
export class PlantService {
  constructor(
    private http: HttpClient,
    private locationService: LocationService,
    private statesService: StatesService
  ) {}

  getPlantData(page = 1, perPage = 25, category = 'All', orderBy = '', keyword = '', packagingType = ''): Observable<{ plants: Product[], pagination: any }> {
    return this.locationService.location.pipe(
      filter(loc => loc !== null),
      first(),
      switchMap(currentLocation =>
        this.http.get<any>(`products/nurseries?page=${page}&per_page=${perPage}&category=${category}&order_by=${orderBy}&keyword=${keyword}&packaging_type=${packagingType}`).pipe(
          map(response => {
            const plants = response.plants.map(rawPlant => {
              const transformedNurseries = rawPlant.nurseries.map(nursery =>
                this.transformNursery(nursery, currentLocation)
              );

              return new Product({
                ...rawPlant,
                locations: transformedNurseries
              });
            });

            return {
              plants: plants,
              pagination: response.pagination
            };
          }),
          catchError(this.handleError)
        )
      )
    );
  }

  getCategories(): Observable<string[]> {
    return this.http.get<any>('category').pipe(
      map(categories => {
        const uniqueCategories = new Set<string>();
        categories.forEach((cat: any) => {
          if (cat.category && cat.category !== '') {
            uniqueCategories.add(cat.category);
          }
        });
        return Array.from(uniqueCategories);
      })
    );
  }

  getPackagingTypes(): Observable<string[]> {
    return this.http.get<any>('packaging');
  }

  // 🔁 Internal Handlers

  private transformNursery(nursery: any, currentLocation: Location): Nursery {
    if (!nursery.lat_long) {
      console.warn(`nursery ${nursery.id} has no lat long`);
    }
    const [lat, lng] = nursery.lat_long.split(',');
    return new Nursery({
      ...nursery,
      lat_lng: {
        lat: parseFloat(lat),
        lng: parseFloat(lng)
      },
      state: this.statesService.getStateFromIdOrDefault(nursery.state_id),
      currentLocation
    });
  }

  private handleNurseryData(res: any) {
    return transformNurseryDataLatLong(res);
  }

  private handleError = (error: any) => {
    console.error('[NurseryService Error]', error);
    return throwError(() => error);
  };
}
